import type { CSSProperties, ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { resolvePalette, useBrandTheme } from './brand-theme';
import { useTheme } from './theme';

export const Card = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) => {
  const C = useTheme();
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '22px 26px',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export const SecTitle = ({
  icon,
  children,
  right,
}: {
  icon?: ReactNode;
  children: ReactNode;
  right?: ReactNode;
}) => {
  const C = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 18 }}>
      {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
      <h2
        style={{
          color: C.text,
          fontSize: 13,
          fontWeight: 700,
          margin: 0,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {children}
      </h2>
      <div style={{ flex: 1, height: 1, background: C.border, marginLeft: 6 }} />
      {right}
    </div>
  );
};

export const Dot = ({ color, size = 9 }: { color: string; size?: number }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      display: 'inline-block',
      marginRight: 7,
      flexShrink: 0,
    }}
  />
);

interface CompareDelta {
  pct: number | null;
}

export const KpiCard = ({
  label,
  value,
  sub,
  accent,
  trend,
  trendInvert,
  compareVal,
  compareDelta,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: string;
  trend?: number;
  trendInvert?: boolean;
  compareVal?: ReactNode;
  compareDelta?: CompareDelta;
}) => {
  const C = useTheme();
  const ac = accent ?? C.accent;
  const good = trendInvert ? (trend ?? 0) < 0 : (trend ?? 0) > 0;
  const cmpGood = trendInvert
    ? compareDelta?.pct !== null && (compareDelta?.pct ?? 0) < 0
    : compareDelta?.pct !== null && (compareDelta?.pct ?? 0) > 0;

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 22px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: ac,
          borderRadius: '12px 12px 0 0',
        }}
      />
      <p
        style={{
          color: C.muted,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </p>
      <p
        style={{
          color: C.text,
          fontSize: 'clamp(22px, 2.4vw, 36px)',
          fontWeight: 600,
          marginBottom: 5,
          fontFamily: 'var(--font-mono, "JetBrains Mono", ui-monospace, monospace)',
          lineHeight: 1,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </p>
      {sub && <p style={{ color: C.sub, fontSize: 13, marginBottom: 4 }}>{sub}</p>}
      {trend !== undefined && trend !== 0 && !compareDelta && (
        <span style={{ fontSize: 12, color: good ? C.good : C.bad, fontWeight: 700 }}>
          {trend > 0 ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}% WoW
        </span>
      )}
      {compareDelta && (
        // ONE LINE, not two. A KPI panel is two grid rows tall and a stacked
        // comparison pushed the delta past the bottom of its cell, where the
        // panel on the next row painted over it — the figure was in the DOM and
        // not on the screen. Found by rendering it, not by reading it.
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: C.muted, fontSize: 11 }}>vs {compareVal}</span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: compareDelta.pct === null ? C.muted : cmpGood ? C.good : C.bad,
            }}
          >
            {compareDelta.pct === null
              ? '—'
              : `${compareDelta.pct > 0 ? '▲' : '▼'} ${Math.abs(compareDelta.pct)}%`}
          </span>
        </div>
      )}
    </div>
  );
};

export const ChartTip = ({
  active,
  payload,
  label,
  fmtVal,
}: {
  active?: boolean;
  payload?: { color: string; name: string; value: number | string }[];
  label?: string | number;
  fmtVal?: (name: string, value: number | string) => string;
}) => {
  const C = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: C.surfaceHigh,
        border: `1px solid ${C.border}`,
        borderRadius: 9,
        padding: '10px 14px',
        fontSize: 13,
        boxShadow: '0 8px 28px rgba(0,0,0,0.3)',
      }}
    >
      <p style={{ color: C.sub, marginBottom: 5, fontWeight: 600 }}>{label}</p>
      {payload.map((p) => (
        <p key={`${p.name}:${p.value}`} style={{ color: p.color, margin: '2px 0' }}>
          <span style={{ color: C.muted }}>{p.name}: </span>
          {fmtVal ? fmtVal(p.name, p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export const MiniBar = ({
  label,
  value,
  pct,
  color,
  opacity = 1,
}: {
  label: ReactNode;
  value: ReactNode;
  pct: number;
  color: string;
  opacity?: number;
}) => {
  const C = useTheme();
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
        <span
          style={{
            color: C.sub,
            fontSize: 12,
            fontFamily: 'var(--font-mono, "JetBrains Mono", ui-monospace, monospace)',
          }}
        >
          {value}
        </span>
      </div>
      <div style={{ background: C.border, borderRadius: 3, height: 5 }}>
        <div
          style={{
            width: `${Math.min(Math.max(pct || 0, 0), 100)}%`,
            height: '100%',
            background: color,
            borderRadius: 3,
            opacity,
          }}
        />
      </div>
    </div>
  );
};

export interface DCol {
  k: string;
  l: string;
  r?: boolean;
}

export const DTable = <Row,>({
  cols,
  rows,
  cellFn,
}: {
  cols: DCol[];
  rows: Row[];
  cellFn: (row: Row, col: DCol, ri: number) => ReactNode;
}) => {
  const C = useTheme();
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          minWidth: 500,
        }}
      >
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.k}
                style={{
                  textAlign: c.r ? 'right' : 'left',
                  color: C.muted,
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  paddingBottom: 10,
                  borderBottom: `1px solid ${C.border}`,
                  paddingLeft: c.r ? 8 : 0,
                  paddingRight: c.r ? 0 : 14,
                  whiteSpace: 'nowrap',
                }}
              >
                {c.l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: generic table rows do not have a stable required id.
            <tr key={ri} style={{ borderBottom: `1px solid ${C.border}` }}>
              {cols.map((col) => (
                <td
                  key={col.k}
                  style={{
                    padding: '11px 0',
                    textAlign: col.r ? 'right' : 'left',
                    paddingLeft: col.r ? 8 : 0,
                    paddingRight: col.r ? 0 : 14,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cellFn(row, col, ri)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const HBar = ({
  data,
  dataKey,
  nameKey,
  colorFn,
  valFmt,
  refLines,
  height = 300,
}: {
  data: Record<string, unknown>[];
  dataKey: string;
  nameKey: string;
  colorFn?: (row: Record<string, unknown>, i: number) => string;
  valFmt?: (v: number) => string;
  refLines?: { v: number; c: string }[];
  height?: number;
}) => {
  const C = useTheme();
  const palette = resolvePalette(useBrandTheme());
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 60, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: C.muted, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={valFmt}
        />
        <YAxis
          dataKey={nameKey}
          type="category"
          tick={{ fill: C.sub, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          width={140}
        />
        <Tooltip
          content={
            <ChartTip
              fmtVal={valFmt ? (_n, v) => valFmt(typeof v === 'number' ? v : Number(v)) : undefined}
            />
          }
        />
        {refLines?.map((r) => (
          <ReferenceLine key={`${r.v}:${r.c}`} x={r.v} stroke={r.c} strokeDasharray="4 4" />
        ))}
        <Bar dataKey={dataKey} radius={[0, 5, 5, 0]}>
          {data.map((row, i) => (
            <Cell
              key={`${String(row[nameKey])}:${String(row[dataKey])}`}
              fill={colorFn ? colorFn(row, i) : (palette[i % palette.length] ?? C.accent)}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const Spinner = ({ msg = '' }: { msg?: string }) => {
  const C = useTheme();
  return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      <div
        style={{
          width: 36,
          height: 36,
          border: `3px solid ${C.border}`,
          borderTop: `3px solid ${C.accent}`,
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      {msg && <p style={{ color: C.sub, fontSize: 14 }}>{msg}</p>}
    </div>
  );
};
